import { Avatar, Button, Table, TextInput } from "@mantine/core";
import {
  IconLayoutGridFilled,
  IconList,
  IconSearch,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import DepartmentCard from "@/components/DepartmentCard";
import { apiUserGetCompany } from "@/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface Department {
  code: string;
  name: string;
  workContent: string;
  company: string;
  leaders: string;
  documentId: string;
}

function DepartmentManagement() {
  const { t } = useTranslation();
  // list or grid
  const [layout, setLayout] = useState("list");
  const [departmentData, setDepartmentData] = useState<Department[]>([]);
  const [filteredData, setFilteredData] = useState<Department[]>([]);
  const navigate = useNavigate();

  const handleAddEmployee = () => {
    navigate("/hrMainPage/addDepartment");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    const filtered = departmentData.filter((item) =>
      item?.name?.toLowerCase().startsWith(value.trim().toLowerCase()),
    );

    if (value === "") {
      setFilteredData(departmentData);
    } else {
      setFilteredData(filtered);
    }
  };

  const handleLayoutChange = (newLayout: string) => {
    setLayout(newLayout);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const id = localStorage.getItem("companyId");
        if (!id) return;
        const result = await apiUserGetCompany({ id });
        setDepartmentData(result?.data?.departments || []);
        setFilteredData(result?.data?.departments || []);
      } catch (error) {
        console.error(t("departmentManagement.errorFetchingData"), error);
      }
    };

    fetchData();
  }, [t]);

  return !departmentData || departmentData?.length === 0 ? (
    <div className="flex min-h-screen h-full items-center justify-center">
      <div className="w-[487px] rounded-xl bg-white px-16 py-20">
        <div className="text-center" style={{ color: "#6F6F6F" }}>
          {t("departmentManagement.noDepartments")}
        </div>
        <div className="flex text-center">
          <Button
            variant="outline"
            color="gray"
            style={{ width: "100%", margin: "1rem" }}
            radius="md"
          >
            {t("departmentManagement.quickCreate")}
          </Button>
          <Button
            variant="outline"
            color="gray"
            style={{ width: "100%", margin: "1rem" }}
            radius="md"
            onClick={handleAddEmployee}
          >
            {t("departmentManagement.manualAdd")}
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div className="min-h-screen">
      <div
        className="flex items-center justify-between px-4"
        style={{ backgroundColor: "#ECECEC" }}
      >
        <div className="flex flex-1 justify-center">
          <TextInput
            leftSection={<IconSearch size={16} />}
            onChange={handleInputChange}
            placeholder={t("departmentManagement.searchPlaceholder")}
          />
        </div>
        <Button
          variant="default"
          style={{ minWidth: "3rem", margin: "1rem" }}
          radius="xl"
          onClick={handleAddEmployee}
        >
          {t("departmentManagement.addDepartment")}
        </Button>
      </div>
      <div className="flex justify-end p-8">
        <IconList
          onClick={() => handleLayoutChange("list")}
          className="mr-2 cursor-pointer"
          size={30}
        />
        <IconLayoutGridFilled
          onClick={() => handleLayoutChange("grid")}
          className="mr-2 cursor-pointer"
          size={30}
        />
      </div>
      {layout === "list" ? (
        <div className="p-4">
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ color: "#6F6F6F" }}>
                <Table.Th>{t("departmentManagement.code")}</Table.Th>
                <Table.Th>{t("departmentManagement.name")}</Table.Th>
                <Table.Th>{t("departmentManagement.description")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody style={{ cursor: "pointer" }}>
              {filteredData.map((element) => {
                const { code = "", name = "", workContent = "" } = element;

                return (
                  <Table.Tr
                    key={code}
                    onClick={() =>
                      navigate(`/hrMainPage/department/${element.documentId}`)
                    }
                  >
                    <Table.Td>{code}</Table.Td>
                    <Table.Td>{name}</Table.Td>
                    <Table.Td>{workContent}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {filteredData.map((data) => (
            <DepartmentCard
              key={data.documentId}
              props={data}
              onClick={() =>
                navigate(`/hrMainPage/department/${data.documentId}`)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default DepartmentManagement;
